import type { CartLine, Menu, Product } from './api';

export function randomId(prefix: string): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return prefix + Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export type CartState = {
  lines: CartLine[];
  note: string;
  name: string;
  phone: string;
  /** Separate opt-in for WhatsApp order updates; sharing a number alone is not consent. */
  waUpdates: boolean;
  detailsSkipped: boolean;
  requestId: string;
};

export function emptyCart(): CartState {
  return { lines: [], note: '', name: '', phone: '', waUpdates: false, detailsSkipped: false, requestId: randomId('r') };
}

export function loadCart(token: string): CartState {
  try {
    const raw = sessionStorage.getItem(`qr-cart:${token}`);
    if (raw) {
      const parsed = JSON.parse(raw) as CartState;
      if (Array.isArray(parsed.lines) && typeof parsed.requestId === 'string') return { ...emptyCart(), ...parsed, requestId: parsed.requestId };
    }
  } catch {
    /* storage unavailable */
  }
  return emptyCart();
}

export function saveCart(token: string, cart: CartState) {
  try {
    sessionStorage.setItem(`qr-cart:${token}`, JSON.stringify(cart));
  } catch {
    /* storage unavailable */
  }
}

/** A line is identified by its full selection, so identical picks merge into one line. */
export function lineKey(l: Pick<CartLine, 'product_id' | 'variant_id' | 'addons' | 'special_instructions'>) {
  const addons = [...l.addons].sort((a, b) => a.id.localeCompare(b.id)).map((a) => `${a.id}x${a.quantity ?? 1}`).join(',');
  return `${l.product_id}|${l.variant_id ?? ''}|${addons}|${l.special_instructions.trim()}`;
}

export function addLine(cart: CartState, line: Omit<CartLine, 'key'>, maxQty: number): CartState {
  const key = lineKey(line);
  const existing = cart.lines.find((l) => l.key === key);
  const lines = existing
    ? cart.lines.map((l) => (l.key === key ? { ...l, quantity: Math.min(maxQty, l.quantity + line.quantity) } : l))
    : [...cart.lines, { ...line, key }];
  // Any change to the cart is a new order attempt; resubmitting the same cart reuses the id.
  return { ...cart, lines, requestId: randomId('r') };
}

export function setQuantity(cart: CartState, key: string, quantity: number, maxQty: number): CartState {
  const lines = quantity <= 0 ? cart.lines.filter((l) => l.key !== key) : cart.lines.map((l) => (l.key === key ? { ...l, quantity: Math.min(maxQty, quantity) } : l));
  return { ...cart, lines, requestId: randomId('r') };
}

export function unitPrice(product: Product, variantId: string | null, addons: { id: string; quantity?: number }[]): number {
  let unit = product.variants.length ? product.variants.find((v) => v.id === variantId)?.price ?? product.price : product.price;
  for (const a of addons) {
    for (const g of product.addon_groups) {
      const found = g.addons.find((x) => x.id === a.id);
      if (found) unit += found.price * (a.quantity ?? 1);
    }
  }
  return unit;
}

export function cartEstimate(menu: Menu, lines: CartLine[]): number {
  const products = new Map(menu.products.map((p) => [p.id, p]));
  let total = 0;
  for (const l of lines) {
    const p = products.get(l.product_id);
    if (p) total += unitPrice(p, l.variant_id, l.addons) * l.quantity;
  }
  return Math.round(total * 100) / 100;
}

/** Returns a message per unmet option-group rule, or [] when the selection is complete. */
export function selectionProblems(product: Product, variantId: string | null, picked: Map<string, number>): string[] {
  const problems: string[] = [];
  if (product.variants.length && !product.variants.some((v) => v.id === variantId)) problems.push('choose_size');
  for (const g of product.addon_groups) {
    const count = g.addons.reduce((s, a) => s + (picked.get(a.id) ?? 0), 0);
    const min = Math.max(g.min, g.required ? 1 : 0);
    if (count < min) problems.push(`min:${g.id}:${min}`);
    if (g.max !== null && g.max > 0 && count > g.max) problems.push(`max:${g.id}:${g.max}`);
  }
  return problems;
}

export function formatMoney(amount: number, currency: string | null, locale?: string): string {
  try {
    return new Intl.NumberFormat(locale, currency ? { style: 'currency', currency } : { minimumFractionDigits: 2 }).format(amount);
  } catch {
    return amount.toFixed(2);
  }
}
